// PDF Page Templates for generating individual page PDFs
// These are simplified versions of each page optimized for PDF generation

import React from 'react';
import './PDFTemplates.css';

// Use absolute URLs for images to ensure they load in detached DOM elements
const getImageUrl = (path) => {
  // In browser, use window.location.origin, fallback to relative path
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return path;
};

const LOGO_URL = '/images/tca-logo.png';
const BANNER_URL = '/images/tca-banner.png';

// Common header for all PDF pages
const PDFHeader = () => (
  <div style={{ textAlign: 'center', marginBottom: '20px', pageBreakInside: 'avoid' }}>
    <img src={getImageUrl(LOGO_URL)} alt="TCA Logo" style={{ height: '80px', marginBottom: '10px' }} crossOrigin="anonymous" />
    <img src={getImageUrl(BANNER_URL)} alt="Banner" style={{ maxWidth: '400px', height: 'auto' }} crossOrigin="anonymous" />
    <h1 style={{ fontSize: '24px', color: '#1e3a8a', marginTop: '15px', fontWeight: 'bold' }}>
      TAX SERVICES AGREEMENT
    </h1>
  </div>
);

// Page 1: Client Information
export const Page1Template = ({ clientData = {} }) => {
  const firstName = clientData.firstName || '';
  const secondName = clientData.secondName || '';
  const middleName = clientData.middleName || '';
  const surname = clientData.surname || '';
  const fullName = [firstName, secondName, middleName, surname].filter(Boolean).join(' ') || 'N/A';
  const today = new Date();
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="pdf-page" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#ffffff' }}>
      <PDFHeader />
      <h2 style={{ fontSize: '18px', color: '#1e3a8a', borderBottom: '2px solid #f97316', paddingBottom: '8px', pageBreakAfter: 'avoid' }}>
        Page 01 of 07: Client Information
      </h2>
      
      <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
        <p><strong>Client Type:</strong> {clientData.clientType === 'new' ? 'New Client' : clientData.clientType === 'existing' ? 'Existing Client' : 'N/A'}</p>
        {clientData.clientType === 'new' && clientData.referralSource && (
          <p><strong>How Did You Hear About Us:</strong> {clientData.referralSource}</p>
        )}
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', border: '1px solid #3b82f6', borderRadius: '8px', pageBreakInside: 'avoid' }}>
        <h3 style={{ color: '#1e40af', marginBottom: '10px' }}>Personal Details</h3>
        <p><strong>Full Name:</strong> {fullName}</p>
        <p><strong>ID Number:</strong> {clientData.idNumber || 'N/A'}</p>
        <p><strong>Tax Number:</strong> {clientData.taxNumber || 'N/A'}</p>
        <p><strong>Email:</strong> {clientData.emailAddress || 'N/A'}</p>
        {clientData.workEmail && <p><strong>Work/Alternative Email:</strong> {clientData.workEmail}</p>}
        <p><strong>Cellphone:</strong> {clientData.cellphoneNumber || 'N/A'}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', pageBreakInside: 'avoid' }}>
        <h3 style={{ color: '#1e3a8a', marginBottom: '10px' }}>Physical Address</h3>
        <p style={{ whiteSpace: 'pre-line' }}>{clientData.physicalAddress || 'N/A'}</p>
      </div>

      <div style={{ marginTop: '30px', pageBreakInside: 'avoid' }}>
        <p><strong>Date:</strong> {fullDate}</p>
      </div>
    </div>
  );
};

// Page 2: SARS Power of Attorney
export const Page2Template = ({ clientData = {} }) => {
  const firstName = clientData.firstName || '';
  const secondName = clientData.secondName || '';
  const middleName = clientData.middleName || '';
  const surname = clientData.surname || '';
  const fullName = [firstName, secondName, middleName, surname].filter(Boolean).join(' ') || 'N/A';
  const today = new Date();
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="pdf-page" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#ffffff' }}>
      <PDFHeader />
      <h2 style={{ fontSize: '18px', color: '#1e3a8a', borderBottom: '2px solid #f97316', paddingBottom: '8px', pageBreakAfter: 'avoid' }}>
        Page 02 of 07: SARS Power of Attorney
      </h2>
      
      <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
        <p><strong>Client:</strong> {fullName}</p>
        <p><strong>ID Number:</strong> {clientData.idNumber || 'N/A'}</p>
        <p><strong>Tax Number:</strong> {clientData.taxNumber || 'N/A'}</p>
        <p><strong>Date:</strong> {fullDate}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', pageBreakInside: 'avoid' }}>
        <h3 style={{ color: '#1e3a8a', marginBottom: '10px' }}>Power of Attorney Authorization</h3>
        <p>I, <strong>{fullName}</strong>, hereby authorize TC Auditors (Pty) Ltd to act as my registered tax practitioner and representative with the South African Revenue Service (SARS).</p>
        <p style={{ marginTop: '10px' }}>This authorization includes:</p>
        <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
          <li>Submitting tax returns on my behalf</li>
          <li>Accessing my tax information on eFiling</li>
          <li>Corresponding with SARS regarding my tax matters</li>
          <li>Receiving and responding to queries from SARS</li>
        </ul>
      </div>

      <div style={{ marginTop: '30px', pageBreakInside: 'avoid' }}>
        <p><strong>Signed at:</strong> {clientData.signedAtPage2 || '_________________'}</p>
        <p><strong>Date:</strong> {fullDate}</p>
        {clientData.signaturePage2 && (
          <div style={{ marginTop: '10px' }}>
            <p><strong>Signature:</strong></p>
            <img src={clientData.signaturePage2} alt="Client Signature" style={{ maxHeight: '80px', border: '1px solid #ccc', padding: '5px' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// Page 3: POPI Consent Agreement
export const Page3Template = ({ clientData = {} }) => {
  const firstName = clientData.firstName || '';
  const secondName = clientData.secondName || '';
  const middleName = clientData.middleName || '';
  const surname = clientData.surname || '';
  const fullName = [firstName, secondName, middleName, surname].filter(Boolean).join(' ') || 'N/A';
  const today = new Date();
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="pdf-page" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#ffffff' }}>
      <PDFHeader />
      <h2 style={{ fontSize: '18px', color: '#1e3a8a', borderBottom: '2px solid #f97316', paddingBottom: '8px', pageBreakAfter: 'avoid' }}>
        Page 03 of 07: POPI Consent Agreement
      </h2>

      <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
        <p><strong>Client:</strong> {fullName}</p>
        <p><strong>ID Number:</strong> {clientData.idNumber || 'N/A'}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', pageBreakInside: 'avoid' }}>
        <h3 style={{ color: '#1e3a8a', marginBottom: '10px' }}>Protection of Personal Information (POPI) Act Consent</h3>
        <p>In terms of the Protection of Personal Information Act 4 of 2013 (POPIA), I hereby consent to the collection, storage, and processing of my personal information by TC Auditors (Pty) Ltd for the following purposes:</p>
        <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
          <li>Rendering of tax services</li>
          <li>Communication regarding my tax matters</li>
          <li>Compliance with legal and regulatory requirements</li>
          <li>Record keeping as required by law</li>
        </ul>
        <p style={{ marginTop: '10px' }}>I understand that my personal information will be kept confidential and will not be shared with third parties without my consent, except as required by law.</p>
      </div>

      <div style={{ marginTop: '30px', pageBreakInside: 'avoid' }}>
        <p><strong>Signed at:</strong> {clientData.signedAtPage3 || '_________________'}</p>
        <p><strong>Date:</strong> {fullDate}</p>
        {clientData.signaturePage3 && (
          <div style={{ marginTop: '10px' }}>
            <p><strong>Signature:</strong></p>
            <img src={clientData.signaturePage3} alt="Client Signature" style={{ maxHeight: '80px', border: '1px solid #ccc', padding: '5px' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// Page 4: Tax Services Agreement
export const Page4Template = ({ clientData = {} }) => {
  const firstName = clientData.firstName || '';
  const secondName = clientData.secondName || '';
  const middleName = clientData.middleName || '';
  const surname = clientData.surname || '';
  const fullName = [firstName, secondName, middleName, surname].filter(Boolean).join(' ') || 'N/A';
  const today = new Date();
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="pdf-page" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#ffffff' }}>
      <PDFHeader />
      <h2 style={{ fontSize: '18px', color: '#1e3a8a', borderBottom: '2px solid #f97316', paddingBottom: '8px', pageBreakAfter: 'avoid' }}>
        Page 04 of 07: Tax Services Agreement
      </h2>

      <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
        <p><strong>Client:</strong> {fullName}</p>
        <p><strong>ID Number:</strong> {clientData.idNumber || 'N/A'}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', pageBreakInside: 'avoid' }}>
        <h3 style={{ color: '#1e3a8a', marginBottom: '10px' }}>Service Agreement Terms</h3>
        <p>TC Auditors (Pty) Ltd agrees to provide tax preparation and submission services as outlined in the service package selected by the client.</p>
        <p style={{ marginTop: '10px' }}><strong>Services Include:</strong></p>
        <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
          <li>Preparation of annual tax returns</li>
          <li>Submission to SARS on your behalf</li>
          <li>Management of SARS queries</li>
          <li>Tax planning advice (where applicable)</li>
        </ul>
        <p style={{ marginTop: '10px' }}><strong>Client Responsibilities:</strong></p>
        <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
          <li>Provide accurate and complete information</li>
          <li>Submit required documents timeously</li>
          <li>Pay agreed fees as per the selected package</li>
        </ul>
      </div>

      <div style={{ marginTop: '30px', pageBreakInside: 'avoid' }}>
        <p><strong>Signed at:</strong> {clientData.signedAtPage4 || '_________________'}</p>
        <p><strong>Date:</strong> {fullDate}</p>
        {clientData.signaturePage4 && (
          <div style={{ marginTop: '10px' }}>
            <p><strong>Signature:</strong></p>
            <img src={clientData.signaturePage4} alt="Client Signature" style={{ maxHeight: '80px', border: '1px solid #ccc', padding: '5px' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// Page 5: Package Selection
export const Page5Template = ({ clientData = {} }) => {
  const firstName = clientData.firstName || '';
  const secondName = clientData.secondName || '';
  const middleName = clientData.middleName || '';
  const surname = clientData.surname || '';
  const fullName = [firstName, secondName, middleName, surname].filter(Boolean).join(' ') || 'N/A';
  const today = new Date();
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="pdf-page" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#ffffff' }}>
      <PDFHeader />
      <h2 style={{ fontSize: '18px', color: '#1e3a8a', borderBottom: '2px solid #f97316', paddingBottom: '8px', pageBreakAfter: 'avoid' }}>
        Page 05 of 07: Package & Urgency Selection
      </h2>

      <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
        <p><strong>Client:</strong> {fullName}</p>
        <p><strong>ID Number:</strong> {clientData.idNumber || 'N/A'}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '8px', pageBreakInside: 'avoid' }}>
        <h3 style={{ color: '#1e3a8a', marginBottom: '10px' }}>Selected Package</h3>
        <p><strong>Package:</strong> {clientData.selectedPackage || 'Not Selected'}</p>
        <p><strong>Urgency:</strong> {clientData.selectedUrgency || 'Not Selected'}</p>
      </div>

      <div style={{ marginTop: '30px', pageBreakInside: 'avoid' }}>
        <p><strong>Signed at:</strong> {clientData.signedAtPage5 || '_________________'}</p>
        <p><strong>Date:</strong> {fullDate}</p>
        {clientData.signaturePage5 && (
          <div style={{ marginTop: '10px' }}>
            <p><strong>Signature:</strong></p>
            <img src={clientData.signaturePage5} alt="Client Signature" style={{ maxHeight: '80px', border: '1px solid #ccc', padding: '5px' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// Page 6: Payment Method Selection
export const Page6Template = ({ clientData = {} }) => {
  const firstName = clientData.firstName || '';
  const secondName = clientData.secondName || '';
  const middleName = clientData.middleName || '';
  const surname = clientData.surname || '';
  const fullName = [firstName, secondName, middleName, surname].filter(Boolean).join(' ') || 'N/A';
  const today = new Date();
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="pdf-page" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#ffffff' }}>
      <PDFHeader />
      <h2 style={{ fontSize: '18px', color: '#1e3a8a', borderBottom: '2px solid #f97316', paddingBottom: '8px', pageBreakAfter: 'avoid' }}>
        Page 06 of 07: Payment Method
      </h2>

      <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
        <p><strong>Client:</strong> {fullName}</p>
        <p><strong>ID Number:</strong> {clientData.idNumber || 'N/A'}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#dcfce7', border: '1px solid #16a34a', borderRadius: '8px', pageBreakInside: 'avoid' }}>
        <h3 style={{ color: '#166534', marginBottom: '10px' }}>Payment Details</h3>
        <p><strong>Selected Payment Method:</strong> {clientData.selectedPaymentMethod || 'Not Selected'}</p>
        <p><strong>Payment Status:</strong> {clientData.paymentCompleted ? 'Completed' : 'Pending'}</p>
      </div>

      <div style={{ marginTop: '30px', pageBreakInside: 'avoid' }}>
        <p><strong>Date:</strong> {fullDate}</p>
      </div>
    </div>
  );
};

// Page 7: Debit Order Mandate (will use the actual form)
export const Page7Template = ({ clientData = {}, formData = {}, signatureData }) => {
  const firstName = clientData.firstName || '';
  const secondName = clientData.secondName || '';
  const middleName = clientData.middleName || '';
  const surname = clientData.surname || '';
  const fullName = [firstName, secondName, middleName, surname].filter(Boolean).join(' ') || 'N/A';
  const today = new Date();
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="pdf-page" style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.6', backgroundColor: '#ffffff' }}>
      <PDFHeader />
      <h2 style={{ fontSize: '18px', color: '#1e3a8a', borderBottom: '2px solid #f97316', paddingBottom: '8px', pageBreakAfter: 'avoid' }}>
        Page 07 of 07: Debit Order Mandate
      </h2>

      <div style={{ marginTop: '20px', pageBreakInside: 'avoid' }}>
        <p><strong>Account Holder:</strong> {formData?.account_holder_name || fullName}</p>
        <p><strong>ID Number:</strong> {formData?.id_number || clientData.idNumber}</p>
        <p><strong>Physical Address:</strong> {formData?.physical_address || clientData.physicalAddress}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', border: '1px solid #3b82f6', borderRadius: '8px' }}>
        <h3 style={{ color: '#1e40af', marginBottom: '10px' }}>Bank Details</h3>
        <p><strong>Bank:</strong> {formData?.bank_name}</p>
        <p><strong>Branch Code:</strong> {formData?.branch_code}</p>
        <p><strong>Account Number:</strong> {formData?.account_number}</p>
        <p><strong>Account Type:</strong> {formData?.account_type}</p>
      </div>

      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px' }}>
        <h3 style={{ color: '#92400e', marginBottom: '10px' }}>Contact Details</h3>
        <p><strong>Contact Number:</strong> {formData?.contact_number || clientData.cellphoneNumber}</p>
        <p><strong>Email:</strong> {formData?.email || clientData.emailAddress}</p>
        <p><strong>Contract Reference:</strong> {formData?.contract_reference}</p>
      </div>

      <div style={{ marginTop: '30px' }}>
        <p><strong>Date of Signing:</strong> {formData?.signing_date || fullDate}</p>
        {signatureData && (
          <div style={{ marginTop: '10px' }}>
            <p><strong>Signature:</strong></p>
            <img src={signatureData} alt="Client Signature" style={{ maxHeight: '80px', border: '1px solid #ccc', padding: '5px' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// Page titles for filenames
export const PAGE_TITLES = {
  1: 'Client_Information',
  2: 'Terms_Authorization',
  3: 'Client_Declaration',
  4: 'Service_Agreement',
  5: 'Package_Selection',
  6: 'Payment_Method',
  7: 'Debit_Order_Mandate'
};

export default {
  Page1Template,
  Page2Template,
  Page3Template,
  Page4Template,
  Page5Template,
  Page6Template,
  Page7Template,
  PAGE_TITLES
};
